import React from 'react';
import styled from 'styled-components/macro';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { parseISO, format } from 'date-fns';
import { Tables, Typography } from '@deeptrue-frontend/ui';
import { useFormatMessage } from 'components/IntlProvider';
import { downloadFileFromUrl } from 'utils/exportToCsv';

const {
  Table,
  TableBody,
  TableHeader,
  TableCell,
  TableRow,
  TablePagination,
  TableFooter,
} = Tables;

interface PaymentsResult {
  id: number;
  created_at: string;
  invoice: {
    id: number;
    invoice_pdf: string;
  };
  stripe_session_id: string;
  paid: boolean;
  email: string;
  address_info: number;
  quantity: number;
  amount: number;
  currency: string;
  type: number;
}

interface Props {
  payments: Array<PaymentsResult>;
  fullHistory?: boolean;
  onChangePage?: (page: number) => void;
  count: number;
  currentPage?: number;
}

const StyledDownloadIcon = styled(DownloadIcon)`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.azureRadiance};
`;

const StatusPill = styled.div<{ isPaid: boolean }>`
  border: 1px solid;
  text-align: center;
  border-radius: 30px;
  width: 118px;
  line-height: 1.5rem;
  border-color: ${({ theme }) => theme.colors.punch};
  color: ${({ theme }) => theme.colors.punch};
  ${({ theme, isPaid }) =>
    isPaid &&
    `
    border-color: ${theme.colors.calmingGreen};
    color: ${theme.colors.calmingGreen};
    `};
`;

const NoData = styled(Typography)`
  text-align: center;
  margin: 1rem;
`;

const HistoryTable = ({
  payments,
  fullHistory = false,
  onChangePage,
  currentPage,
  count,
}: Props) => {
  const t = useFormatMessage();
  const arePaymentsHistory = payments.length > 0;

  return (
    <Table>
      <TableHeader
        cells={[
          t('payments.table.dateOrder', 'Date of order'),
          t('payments.table.credits', 'Credits'),
          t('payments.table.status', 'Status'),
          t('payments.table.paymentType', 'Payment Type'),
          t('payments.table.invoice', 'Invoice'),
        ]}
      />
      <TableBody>
        {arePaymentsHistory ? (
          payments.map((payment: PaymentsResult, index: number) => {
            const isPaid = payment.paid;
            const paidByCardType = 1;
            const isPaidByCard = payment.type === paidByCardType;

            return (
              <TableRow key={index}>
                <TableCell>
                  {format(parseISO(payment.created_at), 'dd-MM-yyyy HH:mm')}
                </TableCell>
                <TableCell>{payment.quantity}</TableCell>
                <TableCell>
                  <StatusPill isPaid={isPaid}>
                    {isPaid
                      ? t('payments.status.paid', 'Paid')
                      : t('payments.status.notPaid', 'Not Paid')}
                  </StatusPill>
                </TableCell>
                <TableCell>
                  {isPaidByCard
                    ? t('payments.type.card', 'Card')
                    : t('payments.type.invoice', 'Invoice')}
                </TableCell>
                <TableCell>
                  {payment.invoice ? (
                    <StyledDownloadIcon
                      onClick={() =>
                        downloadFileFromUrl(
                          payment.invoice.invoice_pdf,
                          `Deeptrue Invoice - order no. ${payment.invoice.id}.pdf`,
                          true
                        )
                      }
                    />
                  ) : (
                    'N/A'
                  )}
                </TableCell>
              </TableRow>
            );
          })
        ) : (
          <TableRow disabledHover>
            <TableCell colSpan="5">
              <NoData>{t('pages.payment.nodata', 'No data')}</NoData>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {fullHistory && onChangePage && (
        <TableFooter>
          <TablePagination
            count={count}
            rowsPerPage={20}
            currentPage={currentPage || 0}
            onChangePage={onChangePage}
          />
        </TableFooter>
      )}
    </Table>
  );
};

export default HistoryTable;
